import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchDeployedBuild } from './deployed-build';
import { showUpdateAvailableToast } from './update-toast';

export { fetchDeployedBuild };

const POLL_INTERVAL_MS = 3 * 60 * 1000; // 3 minutes

// Module-level so the `vite:preloadError` handler (App.tsx, module scope) can
// flag an update without a React context. The hook subscribes and (re-)asserts
// the toast whenever this fires.
let updateAvailable = false;
const listeners = new Set<() => void>();

function markUpdateAvailable() {
  updateAvailable = true;
  for (const listener of listeners) listener();
}

/**
 * Module-scope handler for `vite:preloadError`. A 404'd hashed chunk means this
 * client is referencing a build that no longer exists — surface the update
 * prompt so the user can reload when they're ready. We never force a reload.
 */
export function handlePreloadError(): void {
  markUpdateAvailable();
}

/**
 * Keeps the web client from going super-stale — without ever interrupting the
 * user. Polls /version.json (interval + on focus/visibility) and, when a
 * STRICTLY NEWER build is deployed, shows a persistent, non-blocking,
 * non-dismissable toast with a "Reload" button. The reload only happens when
 * the user clicks it; nothing auto-reloads — but the toast cannot be swiped
 * away either, so Reload is the only way to clear it.
 *
 * Correctness guard: we compare build *time*, not just id, and only prompt when
 * the deployed build is strictly newer than the one we're running. That means a
 * stale CDN edge serving an older version.json can never prompt a user who is
 * already on the latest (or a newer) build.
 *
 * Also refreshes PostHog feature flags on foreground so newly-shipped features
 * can appear even before the user reloads.
 *
 * Mount once, inside the Router + PostHog providers (see App.tsx).
 */
export function useAutoUpdate(): void {
  const posthog = usePostHog();
  const { t } = useTranslation();
  const trackedRef = useRef(false);
  // Bumped on every detection (and by a pre-mount preloadError). Each bump
  // re-asserts the toast; `t` in the toast effect's deps re-translates it if the
  // user switches language while the prompt is up.
  const [assertTick, setAssertTick] = useState(0);

  // Subscribe to the module-level flag. Production only — local dev has no real
  // deploys (or version.json) to detect against.
  useEffect(() => {
    if (!import.meta.env.PROD) return;
    const onChange = () => setAssertTick((n) => n + 1);
    listeners.add(onChange);
    if (updateAvailable) setAssertTick((n) => n + 1); // preloadError before mount
    return () => {
      listeners.delete(onChange);
    };
  }, []);

  // (Re-)assert the persistent, non-dismissable update toast while an update
  // is pending. The stable id keeps it to a single toast, so it's always
  // visible until the user reloads (options + rationale in ./update-toast).
  useEffect(() => {
    if (assertTick === 0 || !updateAvailable) return;
    showUpdateAvailableToast(t);
  }, [assertTick, t]);

  // Detect new deploys.
  useEffect(() => {
    if (!import.meta.env.PROD) return;
    let cancelled = false;

    const check = async () => {
      const deployed = await fetchDeployedBuild();
      if (cancelled || !deployed) return;
      // Strictly newer only — never prompt a client that's already current.
      if (deployed.buildTime <= __APP_BUILD_TIME__) return;

      if (!trackedRef.current) {
        trackedRef.current = true;
        try {
          posthog?.capture?.('web_update_prompt_shown', {
            fromBuildId: __APP_BUILD_ID__,
            fromBuildTime: __APP_BUILD_TIME__,
            toBuildId: deployed.buildId,
            toBuildTime: deployed.buildTime,
          });
        } catch {
          // analytics is best-effort
        }
      }
      markUpdateAvailable();
    };

    const onForeground = () => {
      // Refresh remote config so a freshly-flagged feature can appear without a
      // reload at all.
      try {
        posthog?.reloadFeatureFlags?.();
      } catch {
        // best-effort
      }
      void check();
    };

    const onVisibilityChange = () => {
      if (!document.hidden) onForeground();
    };

    void check();
    const interval = window.setInterval(() => void check(), POLL_INTERVAL_MS);
    window.addEventListener('focus', onForeground);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('focus', onForeground);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [posthog]);
}
